import React, {
 useEffect,
 useRef,
 useState,
 useCallback,
} from'react';
import { gsap } from'../../animations/gsap.config';
import { ScrollTrigger } from'../../animations/gsap.config';
import homeData from'../../data/home.json';
import {
 Phone,
 Mail,
 MapPin,
 Clock,
 Send,
 CheckCircle,
 AlertCircle,
 Sparkles,
 Loader2,
 ExternalLink,
} from'lucide-react';
import { FaLinkedinIn } from'react-icons/fa';

/* ─────────────────────────────────────────────
 FORM FIELD
───────────────────────────────────────────── */
const FormField = ({ field, value, onChange, error }) => {
 const [focused, setFocused] = useState(false);
 const ref = useRef(null);
 const lineRef = useRef(null);

 const handleFocus = () => {
 setFocused(true);
 gsap.to(lineRef.current, {
 scaleX: 1,
 duration: 0.4,
 ease:'power3.out',
 });
 gsap.to(ref.current, {
 borderColor:'rgba(29,78,216,0.5)',
 duration: 0.3,
 });
 };

 const handleBlur = () => {
 setFocused(false);
 if (!value) {
 gsap.to(lineRef.current, {
 scaleX: 0,
 duration: 0.3,
 ease:'power2.in',
 });
 }
 gsap.to(ref.current, {
 borderColor:'rgba(203,213,225,0.3)',
 duration: 0.3,
 });
 };

 const baseClass =`
 w-full bg-transparent text-gray-900 text-sm
 placeholder-gray-500 outline-none resize-none
 transition-colors duration-300
`;

 return (
 <div className={`${field.half ?'' :'col-span-full'} space-y-1.5`}>
 {/* Label */}
 <label className="block text-xs font-semibold text-gray-600 tracking-wide">
 {field.label}
 {field.required && (
 <span className="text-secondary-400 ml-0.5">*</span>
 )}
 </label>

 {/* Input wrapper */}
 <div
 ref={ref}
 className="relative rounded-xl overflow-hidden"
 style={{
 background:'rgba(248,250,252,0.8)',
 border: error
 ?'1px solid rgba(239,68,68,0.4)'
 :'1px solid rgba(203,213,225,0.3)',
 }}
 >
 {field.type ==='textarea' ? (
 <textarea
 id={field.id}
 name={field.id}
 placeholder={field.placeholder}
 rows={5}
 maxLength={field.maxLength}
 required={field.required}
 value={value}
 onChange={onChange}
 onFocus={handleFocus}
 onBlur={handleBlur}
 className={`${baseClass} px-4 py-3`}
 />
 ) : (
 <input
 id={field.id}
 type={field.type}
 name={field.id}
 placeholder={field.placeholder}
 required={field.required}
 value={value}
 onChange={onChange}
 onFocus={handleFocus}
 onBlur={handleBlur}
 className={`${baseClass} px-4 py-3`}
 />
 )}

 {/* Char counter for textarea */}
 {field.type ==='textarea' && field.maxLength && (
 <div className="absolute bottom-2 right-3 text-[10px] text-gray-500 font-mono">
 {value.length} / {field.maxLength}
 </div>
 )}

 {/* Focus bottom line */}
 <div
 ref={lineRef}
 className="absolute bottom-0 left-0 right-0 h-[2px] origin-left"
 style={{
 background:'linear-gradient(90deg, #1d4ed8, #f59e0b)',
 transform:'scaleX(0)',
 }}
 />
 </div>

 {/* Error message */}
 {error && (
 <p className="flex items-center gap-1 text-xs text-red-400">
 <AlertCircle size={11} />
 {error}
 </p>
 )}
 </div>
 );
};

/* ─────────────────────────────────────────────
 CONTACT INFO CARD
───────────────────────────────────────────── */
const InfoCard = ({ icon: Icon, label, children, color, href }) => {
 const ref = useRef(null);

 const handleEnter = () => {
 gsap.to(ref.current, {
 x: 4,
 borderColor:`${color}40`,
 backgroundColor:`${color}06`,
 duration: 0.3,
 ease:'power2.out',
 });
 };
 const handleLeave = () => {
 gsap.to(ref.current, {
 x: 0,
 borderColor:'rgba(203,213,225,0.3)',
 backgroundColor:'rgba(248,250,252,0.8)',
 duration: 0.3,
 ease:'power2.out',
 });
 };

 const Wrapper = href ?'a' :'div';
 const props = href
 ? { href, target: href.startsWith('http') ?'_blank' : undefined, rel:'noopener noreferrer' }
 : {};

 return (
 <Wrapper
 ref={ref}
 {...props}
 onMouseEnter={handleEnter}
 onMouseLeave={handleLeave}
 className="flex items-start gap-4 p-4 rounded-2xl cursor-default
 transition-shadow duration-300"
 style={{
 background:'rgba(248,250,252,0.8)',
 border:'1px solid rgba(203,213,225,0.3)',
 }}
 >
 <div
 className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
 style={{ background:`${color}20`, border:`1px solid ${color}30` }}
 >
 <Icon size={18} style={{ color }} />
 </div>
 <div className="min-w-0">
 <p className="text-[10px] font-semibold tracking-wider text-gray-500 uppercase mb-1">
 {label}
 </p>
 <div className="text-sm text-gray-700 leading-relaxed">
 {children}
 </div>
 </div>
 </Wrapper>
 );
};

/* ─────────────────────────────────────────────
 MAP EMBED
───────────────────────────────────────────── */
const MapEmbed = () => {
 const ref = useRef(null);
 const hasRun = useRef(false);

 useEffect(() => {
 const ctx = gsap.context(() => {
 ScrollTrigger.create({
 trigger: ref.current,
 start:'top 85%',
 onEnter: () => {
 if (hasRun.current) return;
 hasRun.current = true;
 gsap.fromTo(
 ref.current,
 { opacity: 0, scale: 0.97 },
 { opacity: 1, scale: 1, duration: 0.7, ease:'power3.out' },
 );
 },
 });
 }, ref);
 return () => ctx.revert();
 }, []);

 return (
 <div
 ref={ref}
 className="relative rounded-2xl overflow-hidden"
 style={{
 height:'220px',
 border:'1px solid rgba(29,78,216,0.2)',
 opacity: 0,
 }}
 >
 {/* Custom map overlay */}
 <div
 className="absolute inset-0 z-10 pointer-events-none"
 style={{
 background:'linear-gradient(to top, rgba(6,13,36,0.4), transparent 50%)',
 }}
 />

 <iframe
 title="Agora Consulting Location"
 src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d126765.44443490908!2d38.6521022!3d9.0107892!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x164b85cef5ab402d%3A0x8467b6b037a24d49!2sAddis%20Ababa%2C%20Ethiopia!5e0!3m2!1sen!2set!4v1234567890!5m2!1sen!2set"
 width="100%"
 height="100%"
 style={{
 border: 0,
 filter:'invert(90%) hue-rotate(180deg) saturate(0.4) brightness(0.7)',
 }}
 allowFullScreen=""
 loading="lazy"
 referrerPolicy="no-referrer-when-downgrade"
 />

 {/* Location pin overlay */}
 <div className="absolute bottom-4 left-4 z-20">
 <div
 className="glass px-3 py-1.5 rounded-xl flex items-center gap-2
 border border-primary-500/30"
 >
 <MapPin size={12} className="text-secondary-400" />
 <span className="text-xs text-white font-medium">
 Century Executive Tower, Addis Ababa
 </span>
 </div>
 </div>
 </div>
 );
};

/* ─────────────────────────────────────────────
 LEFT INFO PANEL
───────────────────────────────────────────── */
const InfoPanel = ({ data }) => {
 const panelRef = useRef(null);
 const labelRef = useRef(null);
 const headRef = useRef(null);
 const subRef = useRef(null);
 const cardsRef = useRef(null);

 useEffect(() => {
 const ctx = gsap.context(() => {
 const tl = gsap.timeline({
 scrollTrigger: {
 trigger: panelRef.current,
 start:'top 72%',
 },
 });

 tl.fromTo(
 labelRef.current,
 { x: -25, opacity: 0 },
 { x: 0, opacity: 1, duration: 0.5, ease:'power3.out' },
 )
 .fromTo(
 headRef.current?.children ?? [],
 { y: 40, opacity: 0, skewY: 2 },
 {
 y: 0, opacity: 1, skewY: 0,
 stagger: 0.1,
 duration: 0.7,
 ease:'power4.out',
 },
'-=0.2',
 )
 .fromTo(
 subRef.current,
 { y: 15, opacity: 0 },
 { y: 0, opacity: 1, duration: 0.5, ease:'power3.out' },
'-=0.3',
 )
 .fromTo(
 cardsRef.current?.children ?? [],
 { y: 20, opacity: 0 },
 {
 y: 0, opacity: 1,
 stagger: 0.1,
 duration: 0.5,
 ease:'power3.out',
 },
'-=0.2',
 );
 }, panelRef);
 return () => ctx.revert();
 }, []);

 return (
 <div ref={panelRef} className="space-y-8">

 {/* Label */}
 <div ref={labelRef} style={{ opacity: 0 }}>
 <div className="section-label inline-flex">
 <Sparkles size={12} className="text-secondary-400" />
 {data.sectionLabel}
 </div>
 </div>

 {/* Headline */}
 <div ref={headRef} className="overflow-hidden space-y-1">
 <h2
 className="font-display font-black text-gray-900 leading-tight"
 style={{ fontSize:'clamp(2rem, 3.5vw, 3rem)' }}
 >
 {data.headline}
 </h2>
 <h2
 className="font-display font-black text-gradient-primary leading-tight"
 style={{ fontSize:'clamp(2rem, 3.5vw, 3rem)' }}
 >
 {data.headlineAccent}
 </h2>
 </div>

 {/* Subheadline */}
 <p
 ref={subRef}
 className="text-gray-600 leading-relaxed text-[15px] max-w-sm"
 style={{ opacity: 0 }}
 >
 {data.subheadline}
 </p>

 {/* Info cards */}
 <div ref={cardsRef} className="space-y-3">
 {/* Phones */}
 <InfoCard
 icon={Phone}
 label="Phone"
 color="#3b82f6"
 href={`tel:${data.info.phones[0].value}`}
 >
 {data.info.phones.map((p, i) => (
 <div key={i} className="flex items-center gap-2">
 <span className="text-[10px] text-slate-500">{p.label}:</span>
 <a
 href={`tel:${p.value}`}
 className="hover:text-secondary-400 transition-colors duration-300"
 >
 {p.value}
 </a>
 </div>
 ))}
 </InfoCard>

 {/* Email */}
 <InfoCard
 icon={Mail}
 label="Email"
 color="#f59e0b"
 href={`mailto:${data.info.email}`}
 >
 <a
 href={`mailto:${data.info.email}`}
 className="hover:text-secondary-400 transition-colors duration-300
 break-all"
 >
 {data.info.email}
 </a>
 </InfoCard>

 {/* Address */}
 <InfoCard icon={MapPin} label="Address" color="#6366f1">
 <p>{data.info.address}</p>
 <p className="text-slate-400 text-xs">{data.info.area}</p>
 <p className="text-slate-500 text-xs">{data.info.poBox}</p>
 </InfoCard>

 {/* Hours */}
 <InfoCard icon={Clock} label="Office Hours" color="#10b981">
 <p>{data.info.hours}</p>
 </InfoCard>

 {/* LinkedIn */}
 <a
 href={data.info.linkedin}
 target="_blank"
 rel="noopener noreferrer"
 className="flex items-center gap-3 px-4 py-3 rounded-xl
 border border-primary-500/30 bg-primary-600/10
 hover:bg-primary-600/20 hover:border-primary-500/50
 transition-all duration-300 group"
 >
 <FaLinkedinIn size={16} className="text-primary-400" />
 <span className="text-sm text-gray-700 group-hover:text-gray-900
 transition-colors duration-300">
 Follow us on LinkedIn
 </span>
 <ExternalLink size={12} className="text-gray-500 ml-auto" />
 </a>
 </div>

 {/* Map */}
 <MapEmbed />
 </div>
 );
};

/* ─────────────────────────────────────────────
 CONTACT FORM
───────────────────────────────────────────── */
const ContactForm = ({ formData }) => {
 const formRef = useRef(null);
 const successRef = useRef(null);

 const [values, setValues] = useState({});
 const [errors, setErrors] = useState({});
 const [status, setStatus] = useState('idle'); // idle | loading | success | error
 const [charCount, setCharCount] = useState(0);

 /* Scroll reveal */
 useEffect(() => {
 const ctx = gsap.context(() => {
 gsap.fromTo(
 formRef.current,
 { x: 40, opacity: 0 },
 {
 x: 0, opacity: 1,
 duration: 0.9,
 ease:'power3.out',
 scrollTrigger: {
 trigger: formRef.current,
 start:'top 72%',
 },
 },
 );
 }, formRef);
 return () => ctx.revert();
 }, []);

 const validate = useCallback(() => {
 const errs = {};
 formData.fields.forEach((f) => {
 if (f.required && !values[f.id]) {
 errs[f.id] =`${f.label} is required`;
 }
 if (f.type ==='email' && values[f.id] &&
 !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values[f.id])) {
 errs[f.id] ='Please enter a valid email';
 }
 });
 return errs;
 }, [values, formData.fields]);

 const handleChange = useCallback((e) => {
 const { name, value } = e.target;
 setValues((p) => ({ ...p, [name]: value }));
 if (name ==='message') setCharCount(value.length);
 if (errors[name]) {
 setErrors((p) => ({ ...p, [name]:'' }));
 }
 }, [errors]);

 const handleSubmit = useCallback(async (e) => {
 e.preventDefault();
 const errs = validate();
 if (Object.keys(errs).length) {
 setErrors(errs);
 /* Shake the form */
 gsap.to(formRef.current, {
 x: [-8, 8, -6, 6, -3, 3, 0],
 duration: 0.4,
 ease:'none',
 });
 return;
 }

 setStatus('loading');

 /* Simulate API call */
 await new Promise((r) => setTimeout(r, 1500));

 setStatus('success');

 /* Success animation */
 gsap.fromTo(
 successRef.current,
 { scale: 0.9, opacity: 0, y: 20 },
 { scale: 1, opacity: 1, y: 0, duration: 0.6, ease:'back.out(1.7)' },
 );
 }, [validate]);

 if (status ==='success') {
 return (
 <div
 ref={successRef}
 className="h-full flex flex-col items-center justify-center
 text-center p-10 rounded-3xl space-y-5"
 style={{
 background:'rgba(16,185,129,0.05)',
 border:'1px solid rgba(16,185,129,0.2)',
 opacity: 0,
 }}
 >
 <div className="w-20 h-20 rounded-full bg-emerald-500/20
 border-2 border-emerald-500/40 flex items-center justify-center
 mx-auto">
 <CheckCircle size={36} className="text-emerald-400" />
 </div>
 <div className="space-y-2">
 <h3 className="text-xl font-bold text-white">Message Sent!</h3>
 <p className="text-slate-400 text-sm max-w-xs">
 {formData.successMessage}
 </p>
 </div>
 <button
 onClick={() => { setStatus('idle'); setValues({}); }}
 className="text-sm text-primary-400 hover:text-primary-300
 transition-colors duration-300 underline"
 >
 Send another message
 </button>
 </div>
 );
 }

 return (
 <div
 ref={formRef}
 className="rounded-3xl p-7 sm:p-8 space-y-6"
 style={{
 background:'rgba(248,250,252,0.8)',
 border:'1px solid rgba(203,213,225,0.3)',
 backdropFilter:'blur(16px)',
 opacity: 0,
 }}
 >
 {/* Form header */}
 <div className="space-y-1">
 <h3 className="font-bold text-gray-900 text-xl">Send Us a Message</h3>
 <p className="text-gray-600 text-sm">
 We'll respond within 24 hours.
 </p>
 </div>

 {/* Divider */}
 <div
 className="h-px w-full"
 style={{
 background:
'linear-gradient(to right, rgba(29,78,216,0.4), rgba(245,158,11,0.2), transparent)',
 }}
 />

 {/* Form fields */}
 <form onSubmit={handleSubmit} noValidate>
 <div className="grid grid-cols-2 gap-4">
 {formData.fields.map((field) => (
 <FormField
 key={field.id}
 field={field}
 value={values[field.id] ||''}
 onChange={handleChange}
 error={errors[field.id]}
 />
 ))}
 </div>

 {/* Submit */}
 <div className="mt-6">
 <button
 type="submit"
 disabled={status ==='loading'}
 className="group w-full relative inline-flex items-center
 justify-center gap-2.5 px-7 py-4
 bg-primary-600 hover:bg-primary-500 text-white
 font-semibold rounded-xl text-sm overflow-hidden
 transition-all duration-300 hover:shadow-glow-primary
 hover:-translate-y-0.5 disabled:opacity-60
 disabled:cursor-not-allowed disabled:hover:translate-y-0"
 >
 {/* Shine */}
 <span
 className="absolute inset-0 translate-x-[-100%]
 group-hover:translate-x-[100%] transition-transform duration-700
 bg-gradient-to-r from-transparent via-white/15 to-transparent"
 />
 {status ==='loading' ? (
 <>
 <Loader2 size={16} className="animate-spin" />
 <span>Sending...</span>
 </>
 ) : (
 <>
 <Send size={15} />
 <span>{formData.submitText}</span>
 </>
 )}
 </button>
 </div>
 </form>
 </div>
 );
};

/* ─────────────────────────────────────────────
 BG EFFECTS
───────────────────────────────────────────── */
const ContactBg = () => {
 const meshRef = useRef(null);

 useEffect(() => {
 gsap.to(meshRef.current, {
 opacity: 0.7,
 scale: 1.08,
 duration: 5,
 ease:'sine.inOut',
 repeat: -1,
 yoyo: true,
 });
 }, []);

 return (
 <div className="absolute inset-0 pointer-events-none overflow-hidden">
 <div className="absolute inset-0 dot-pattern opacity-[0.05]" />
 <div
 ref={meshRef}
 className="absolute inset-0"
 style={{
 background:`
 radial-gradient(ellipse 60% 50% at 20% 50%,
 rgba(29,78,216,0.08) 0%, transparent 60%),
 radial-gradient(ellipse 40% 40% at 80% 60%,
 rgba(245,158,11,0.05) 0%, transparent 55%)
`,
 opacity: 0.5,
 }}
 />
 {/* Top / bottom fades */}
 <div
 className="absolute top-0 left-0 right-0 h-28"
 style={{ background:'linear-gradient(to bottom, #ffffff, transparent)' }}
 />
 <div
 className="absolute bottom-0 left-0 right-0 h-28"
 style={{ background:'linear-gradient(to top, #ffffff, transparent)' }}
 />
 </div>
 );
};

/* ─────────────────────────────────────────────
 MAIN CONTACT SECTION
───────────────────────────────────────────── */
const ContactSection = () => {
 const { contact } = homeData;
 const sectionRef = useRef(null);

 return (
 <section
 ref={sectionRef}
 id="contact"
 className="relative overflow-hidden section-py"
 style={{
 background:'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
 }}
 >
 <ContactBg />

 {/* Vertical labels */}
 <div
 className="absolute left-4 top-1/2 -translate-y-1/2 hidden xl:flex
 flex-col items-center gap-3 pointer-events-none z-10"
 >
 <div
 className="w-px h-16"
 style={{ background:'linear-gradient(to bottom, transparent, rgba(29,78,216,0.4))' }}
 />
 <p
 className="text-[10px] font-semibold tracking-[0.4em] text-slate-700 uppercase"
 style={{ writingMode:'vertical-lr' }}
 >
 Contact
 </p>
 <div
 className="w-px h-16"
 style={{ background:'linear-gradient(to top, transparent, rgba(29,78,216,0.4))' }}
 />
 </div>

 <div className="container-fluid relative z-20">
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 xl:gap-20">

 {/* Left — Info */}
 <InfoPanel data={contact} />

 {/* Right — Form */}
 <ContactForm formData={contact.form} />
 </div>
 </div>

 {/* Bottom accent */}
 <div className="container-fluid relative z-20 mt-16">
 <div
 className="h-px w-full"
 style={{
 background:
'linear-gradient(to right, transparent, rgba(29,78,216,0.3), rgba(245,158,11,0.3), transparent)',
 }}
 />
 </div>
 </section>
 );
};

export default ContactSection;